import { FunctionComponent, useState } from 'react';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import classnames from 'classnames';

import { Routes } from 'types/routes';

import { withVariables } from 'utils/string';

import Breadcrumbs from 'components/layout/Breadcrumbs';
import LayoutContainer from 'components/layout/LayoutContainer';
import Spinner from 'components/layout/Spinner/component';
import JobBoxWithDescription from 'components/modules/public/layout/jobs/JobBoxWithDescription';

import JobBenefits from './blocks/JobBenefits';
import JobDescription from './blocks/JobDescription';
import JobFooter from './blocks/JobFooter';
import JobHeader from './blocks/JobHeader';
import JobHeaderMobile from './blocks/JobHeaderMobile';
import JobLanguages from './blocks/JobLanguages';
import JobLocation from './blocks/JobLocation';
import JobSkills from './blocks/JobSkills';
import StyledComponent from './styles';
import { Props } from './types';

const DynamicJobPreLoginModal = dynamic(
    () => import('components/modules/public/layout/modals/JobPreLoginModal'),
    { loading: () => <Spinner /> }
);

const PublicPageJobSectionJob: FunctionComponent<Props> = ({
    job,
    jobCity,
    header,
    headerMobile,
    jobBoxWithDescriptionProps,
    description,
    jobSkills,
    jobBenefits,
    footer,
    modalContentProps,
    jobLanguages,
    jobLocation,
    state,
    breadcrumbs,
}) => {
    const [isPreLoginModalVisible, setIsPreLoginModalVisible]: [boolean, Function] = useState(false);
    const router = useRouter();
    const { authToken } = state;

    const onApply = (loginFree?: boolean) => {
        if(!authToken && loginFree) return router.push(withVariables(Routes.PublicJobAplication, { slug: job?.slug, jobCitySlug: jobCity?.id }));

        if(!authToken) return setIsPreLoginModalVisible(true);

        if(authToken) return router.push(withVariables(Routes.PublicJobAplication, { slug: job?.slug, jobCitySlug: jobCity?.id }));
    };

    const applicationButtonVisible = router.route === Routes.PublicJobAplication ? false : true;

    return (
        <StyledComponent className={classnames(
            'public-page-job-section-job',
            { 'closed': job?.isClosed }
        )}
        >
            <LayoutContainer className="content-container">
                <div className='header-cont'>
                <Breadcrumbs
                    routes={breadcrumbs}
                />
                <JobHeader
                    {...header}
                    job={job}
                />
                </div>
                {!job?.isClosed && (
                    <JobHeaderMobile
                        {...headerMobile}
                        job={job}
                        jobCity={jobCity}
                    />
                )}
                <JobBoxWithDescription
                    {...jobBoxWithDescriptionProps}
                    job={job}
                    jobCity={jobCity}
                    onApply={onApply}
                    applicationButtonVisible={applicationButtonVisible}
                />
                {jobCity?.companyFullAddress && (
                    <JobLocation
                        {...jobLocation}
                        jobCity={jobCity}
                    />
                )}
                {job?.dutiesDescription && (
                    <JobDescription
                        {...description}
                        headline={job.jobHeadlines && Array.isArray(job.jobHeadlines) && job.jobHeadlines[0] !== undefined ? job.jobHeadlines[0] : description?.duties}
                        description={job?.dutiesDescription}
                    />
                )}
                {job?.expectationsDescription && (
                    <JobDescription
                        {...description}
                        headline={job.jobHeadlines && Array.isArray(job.jobHeadlines) && job.jobHeadlines[1] !== undefined ? job.jobHeadlines[1] : description?.expectations}
                        description={job?.expectationsDescription}
                    />
                )}
                {Array.isArray(job?.jobLanguages) && job?.jobLanguages?.length > 0 && (
                    <JobLanguages
                        {...jobLanguages}
                        headline={description?.languages}
                        list={job?.jobLanguages}
                        types={jobLanguages.types}
                        single={jobLanguages.single}
                    />
                )}
                {job?.offerDescription && (
                    <JobDescription
                        {...description}
                        headline={job.jobHeadlines && Array.isArray(job.jobHeadlines) && job.jobHeadlines[2] !== undefined ? job.jobHeadlines[2] : description?.offer}
                        description={job?.offerDescription}
                    />
                )}
                {Array.isArray(job?.jobSkills) && job?.jobSkills?.length > 0 && (
                    <JobSkills
                        {...jobSkills}
                        list={job?.jobSkills}
                    />
                )}
                {Array.isArray(job?.jobBenefits) && job?.jobBenefits?.length > 0 && (
                    <JobBenefits
                        {...jobBenefits}
                        list={job?.jobBenefits}
                    />
                )}
                {job?.stagesDescription && (
                    <JobDescription
                        {...description}
                        headline={description?.stages}
                        description={job?.stagesDescription}
                        isOrder={true}
                    />
                )}
                {job?.legalDescription && (
                    <JobDescription
                        {...description}
                        headline={description?.legalDescription}
                        description={job?.legalDescription}
                        isOrder={true}
                    />
                )}
                <JobFooter
                    {...footer}
                    job={job}
                    jobCity={jobCity}
                    onApply={onApply}
                />
            </LayoutContainer>

            {isPreLoginModalVisible && (
                <DynamicJobPreLoginModal
                    {...modalContentProps}
                    modalProps={{
                        onClose: () => setIsPreLoginModalVisible(false),
                        title: modalContentProps?.modalProps?.title,
                    }}
                    onApply={onApply}
                />
            )}
        </StyledComponent>
    );
};

export default PublicPageJobSectionJob;